import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { Grid } from '@material-ui/core';
import { ContasPagarCodigoBarras, ContasPagarUnica } from 'app/main/contaspagar/components';
import { schemaAproveitamentoCredito } from 'app/main/contaspagar/container/ContasPagarSchema';
import { cancelarAlert } from 'app/services/alerts';
import conciliacaoApi from 'app/services/api/conciliacaoApi';
import empresaApi from 'app/services/api/empresaApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaArrowCircleDown } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ConciliacaoContext } from '../ConciliacaoContext';
import { ConciliacaoModal } from './ConciliacaoModal';

const MOMENT_HOJE = moment();

const CadastrarContaPagarModal = ({ origemConciliacao }) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [conciliacao, setConciliacao] = useContext(ConciliacaoContext).conciliacao;
  const [conciliacoes, setConciliacoes] = useContext(ConciliacaoContext).conciliacoes;
  const [contaPagar, setContaPagar] = useContext(ConciliacaoContext).contaPagar;
  const [empresaIbsCbs, setEmpresaIbsCbs] = useState(false);

  const CONCILIACAO = 'Conciliação';

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  useEffect(() => {
    const carregarEmpresaIbsCbs = async () => {
      try {
        const empresaId = getEmpresaContexto();
        const empresa = await empresaApi.buscar(empresaId);
        if (empresa?.data?.ibsCbs) {
          setEmpresaIbsCbs(true);
        }
      } catch (error) {
        setEmpresaIbsCbs(false);
      }
    };
    carregarEmpresaIbsCbs();
  }, []);

  const close = () => {
    setConciliacao({});
    setContaPagar({});
    formik.resetForm(getInitialValues({}));
    setModal('');
  };

  const handleCloseModal = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const getInitialValues = (contaPagar) => ({
    ...contaPagar,
    origemLancamento: contaPagar?.origemLancamento || 'CONCILIACAO',
    dataVencimento: contaPagar?.dataVencimento || MOMENT_HOJE,
    dataEmissao: contaPagar?.dataEmissao || null,
    tipoDespesa: contaPagar?.tipoDespesa || 'UNICA',
    competencia: contaPagar?.competencia || MOMENT_HOJE,
    fornecedor: contaPagar?.fornecedor || {},
    favorecido: contaPagar?.favorecido || null,
    favorecidoDiferenteFornecedor: contaPagar?.favorecidoDiferenteFornecedor || false,
    categorias: contaPagar?.categorias || [{}],
    repetirLancamento: false,
    gerarParcelasCount: 0,
    aproveitamentoCreditoAtivo: !!contaPagar?.aproveitamentoCredito?.id || false,
    aproveitamentoCredito: contaPagar?.aproveitamentoCredito || null,
  });

  const onSubmitForm = async (contaPagar) => {
    try {
      setLoading(true);
      if (contaPagar.id) {
        eventReactGA(CONCILIACAO, 'Atualizar conta a pagar');
        const retorno = await conciliacaoApi.editarContaPagar(conciliacao.id, contaPagar, false);
        aplicarDadosAtualizados(retorno.data);

        setLoading(false);
        if (origemCompra && retorno?.data?.atributoOrigemCompraAlterado) {
          close();
          showSuccess('Conta a pagar editada com sucesso. As alterações feitas refletirão na compra.');
        } else {
          close();
          showSuccess('Conta a pagar editada com sucesso.');
        }
      } else {
        eventReactGA(CONCILIACAO, 'Adicionar conta a pagar');
        const retorno = await conciliacaoApi.cadastrarContaPagar(conciliacao.id, contaPagar, false);
        aplicarDadosAtualizados(retorno.data);
        setLoading(false);
        close();
        showSuccess('Conta a pagar salva com sucesso.');
      }
    } catch (err) {
      if (err?.response?.data?.errorKey === 'conta.contaDuplicada') {
        cancelarAlert({
          opcoes: {
            title:
              'O número do documento fiscal informado já foi cadastrado para este fornecedor. Tem certeza que deseja continuar?',
            text: '',
          },
          aceitarTexto: 'OK',
          recusarTexto: 'Cancelar',
          confirmar: async () => {
            setLoading(true);
            if (contaPagar.id) {
              eventReactGA(CONCILIACAO, 'Atualizar conta a pagar');
              const retorno = await conciliacaoApi.editarContaPagar(conciliacao.id, contaPagar, true);
              aplicarDadosAtualizados(retorno.data);

              setLoading(false);
              if (origemCompra && retorno?.data?.atributoOrigemCompraAlterado) {
                close();
                showSuccess('Conta a pagar editada com sucesso. As alterações feitas refletirão na compra.');
              } else {
                close();
                showSuccess('Conta a pagar editada com sucesso.');
              }
            } else {
              eventReactGA(CONCILIACAO, 'Adicionar conta a pagar');
              const retorno = await conciliacaoApi.cadastrarContaPagar(conciliacao.id, contaPagar, true);
              aplicarDadosAtualizados(retorno.data);
              setLoading(false);
              close();
              showSuccess('Conta a pagar salva com sucesso.');
            }
          },
        });
      } else {
        apiUtils.handleApiError(err);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const alteraDespesas = (despesas, contaPagar) => {
    let isEdicao = despesas?.some((despesa) => despesa.id === contaPagar.id);

    if (isEdicao) {
      despesas = despesas.map((conta) => {
        if (conta.id === contaPagar.id) {
          return montaEstruturaDoLancamento(contaPagar);
        }
        return conta;
      });
    } else {
      despesas === undefined && (despesas = []);
      despesas.push(montaEstruturaDoLancamento(contaPagar));
    }
    return despesas.filter((despesa) => despesa.fornecedor);
  };

  const aplicarDadosAtualizados = (contaPagar) => {
    let listaAtualizada = conciliacoes.map((conciliacaoAux) => {
      if (conciliacaoAux.id === conciliacao.id) {
        conciliacaoAux.contaPagarList = alteraDespesas(conciliacaoAux.contaPagarList, contaPagar);
      }

      return conciliacaoAux;
    });

    setConciliacoes(listaAtualizada);
  };

  const montaEstruturaDoLancamento = (conta) => {
    return {
      id: conta.id,
      fornecedor: conta.fornecedor,
      baixa: conta.baixa,
      categorias: conta?.categorias,
      valor: conta.situacao === 'BAIXADA' ? conta.valorPago : conta.valorLiquido,
      formulario: false,
    };
  };

  const schemaFornecedor = yup
    .object()
    .nullable()
    .required(mensagemCampoObrigatorio('o fornecedor'))
    .test('fornecedor', mensagemCampoObrigatorio('o fornecedor'), function(fornecedor) {
      return !!fornecedor && !!fornecedor?.id;
    });

  const schemaCategorias = yup
    .array()
    .min(1)
    .of(
      yup.object().shape({
        classe: yup
          .object()
          .nullable()
          .required(mensagemCampoObrigatorio('a categoria'))
          .test('classe', mensagemCampoObrigatorio('a categoria'), function(classe) {
            return !!classe && !!classe?.id;
          }),
        valorBruto: yup
          .number()
          .nullable()
          .required(mensagemCampoObrigatorio('o valor bruto')),
        descricao: yup.string().nullable(),
      })
    );

  const isRetencaoPreenchida = (dados) => {
    const isPreenchido = !!(
      dados.imposto?.id ||
      dados.dataVencimento ||
      (dados.valor !== null && dados.valor !== undefined && dados.valor > 0) ||
      dados.contribuinte ||
      dados.formaPagamento ||
      dados.linhaDigitavel ||
      dados.conta?.id
    );
    return isPreenchido;
  };

  const schemaRetencoes = yup.array().of(
    yup.object().shape({
      imposto: yup
        .object()
        .nullable()
        .test('imposto', mensagemCampoObrigatorio('o imposto'), function(imposto) {
          return isRetencaoPreenchida(this.parent) ? !!imposto?.id : true;
        }),
      dataVencimento: yup
        .date()
        .nullable()
        .test('dataVencimento', mensagemCampoObrigatorio('a data de vencimento'), function(dataVencimento) {
          return isRetencaoPreenchida(this.parent) ? !!dataVencimento : true;
        }),
      valor: yup
        .number()
        .nullable()
        .test('valor', mensagemCampoObrigatorio('o valor'), function(valor) {
          return isRetencaoPreenchida(this.parent) ? valor !== null && valor !== undefined && valor > 0 : true;
        }),
      contribuinte: yup.string().nullable(),
      formaPagamento: yup.string().nullable(),
      linhaDigitavel: yup.string().nullable(),
    })
  );

  const schemaRecorrencias = yup.object().shape({
    frequencia: yup
      .string()
      .nullable()
      .test('frequencia', mensagemCampoObrigatorio('a frequência'), function(frequencia) {
        return !this.parent?.repetirLancamento ? true : !!frequencia;
      }),

    quantidade: yup
      .number()
      .nullable()
      .test('quantidade', mensagemCampoObrigatorio('a quantidade'), function(quantidade) {
        return !this.parent?.repetirLancamento
          ? true
          : quantidade !== null && quantidade !== undefined && quantidade > 0;
      }),
  });

  const schemaDespesaUnica = yup.object().shape({
    competencia: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a competência')),

    fornecedor: schemaFornecedor,
    favorecidoDiferenteFornecedor: yup.bool(),
    favorecido: yup
      .object()
      .nullable()
      .test('favorecido', mensagemCampoObrigatorio('o favorecido'), function(favorecido) {
        return this.parent?.favorecidoDiferenteFornecedor ? Boolean(favorecido) : true;
      }),

    categorias: schemaCategorias,
    dataVencimento: yup
      .date()
      .nullable()
      .required(mensagemCampoObrigatorio('a data do vencimento')),

    conta: yup
      .object()
      .nullable()
      .test('conta', mensagemCampoObrigatorio('a conta'), function(conta) {
        const contaBaixa = this.parent?.baixa?.conta?.id || conta?.id;

        const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;

        if (dataPagamentoBaixa) {
          return !!contaBaixa;
        }
        return true;
      })
      .shape({ id: yup.string() }),
    formaPagamento: yup
      .string()
      .nullable()
      .test('formaPagamento', mensagemCampoObrigatorio('a forma de pagamento'), function(formaPagamento) {
        const formaPagamentoBaixa = this.parent?.baixa?.formaPagamento || formaPagamento;
        const dataPagamentoBaixa = this.parent?.baixa?.dataPagamento;

        if (dataPagamentoBaixa) {
          return !!formaPagamentoBaixa;
        }

        return true;
      }),
    documentoFiscal: yup.string().nullable(),
    observacao: yup
      .string()
      .nullable()
      .max(500),
    dataEmissao: yup.object().nullable(),
    linhaDigitavel: yup.string().nullable(),
    retencoes: schemaRetencoes,
    recorrencia: schemaRecorrencias,
    aproveitamentoCredito: schemaAproveitamentoCredito,
  });

  const formik = useFormik({
    initialValues: getInitialValues(contaPagar),
    validationSchema: schemaDespesaUnica,
    onSubmit: onSubmitForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const isEdicao = !!contaPagar?.id;
  const origemCompraProduto = formik?.values?.origemLancamento === 'COMPRA_PRODUTO';
  const origemCompra = origemCompraProduto || formik?.values?.origemLancamento === 'COMPRA_SERVICO';

  const { resetForm } = formik;
  useEffect(() => {
    resetForm(getInitialValues(contaPagar));
  }, [contaPagar, resetForm]);

  return (
    <SimpleDialog fullScreen open={modal === ConciliacaoModal.cadastrarContaPagarModal} onClose={handleCloseModal}>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Lançamento de contas a pagar',
            icon: <FaArrowCircleDown />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCloseModal}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full" style={{ margin: '15px 0px' }}>
              <Grid container spacing={3}>
                {contaPagar?.tipoDespesa === 'LINHA_DIGITAVEL' ? (
                  <ContasPagarCodigoBarras
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    errors={formik.errors}
                    touched={formik.touched}
                    values={formik.values}
                    origemConciliacao={origemConciliacao}
                    empresaIbsCbs={empresaIbsCbs}
                  />
                ) : (
                  <ContasPagarUnica
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    errors={formik.errors}
                    touched={formik.touched}
                    values={formik.values}
                    isEdicao={isEdicao}
                    origemCompra={origemCompra}
                    origemConciliacao={origemConciliacao}
                    empresaIbsCbs={empresaIbsCbs}
                  />
                )}
              </Grid>
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CadastrarContaPagarModal;
